<script lang="ts">
	//Event dispatcher
	import { createEventDispatcher } from 'svelte';
	//Design system components
	import { ActionsContainer, AdvancedCard, IconButton, LabelList } from '@Shelly/shelly-design-system';
	// Service
	import { format } from 'svelte-i18n';
	import { getTimeFormat } from 'src/helpers/date_helper';
	import { kelvin_to_rgb } from 'src/helpers/utilities';

	export let nightMode,
		disabled = true;

	const dispatcher = createEventDispatcher();

	//Making time values reactive - watch nightMode prop directly to ensure reactivity
	$: activeBetween = nightMode?.active_between;
	$: fromHours = activeBetween?.[0]?.split(':')[0] || '00';
	$: fromMins = activeBetween?.[0]?.split(':')[1] || '00';
	$: toHours = activeBetween?.[1]?.split(':')[0] || '00';
	$: toMins = activeBetween?.[1]?.split(':')[1] || '00';
	//Making color values reactive
	$: white = nightMode?.white;
	$: rgb = nightMode?.rgb;
	$: ct = nightMode?.ct;

	$: nightModeConvertedTimeFrom = getTimeFormat(fromHours, fromMins);
	$: nightModeConvertedTimeTo = getTimeFormat(toHours, toMins);

	$: nightModeConvertedTime = activeBetween?.some((time) => typeof time === 'object')
		? `${getTimeFormat(0, 0)} - ${getTimeFormat(0, 0)}`
		: `${nightModeConvertedTimeFrom} - ${nightModeConvertedTimeTo}`;

	$: timeObject = {
		value: nightModeConvertedTime,
		icon: 'fas fa-moon',
		type: undefined,
	};

	$: brightnessObject =
		typeof nightMode?.brightness !== 'undefined' && nightMode?.brightness !== null
			? {
					value: nightMode.brightness,
					icon: 'fas fa-sun',
					type: undefined,
					name: 'brightness',
				}
			: undefined;

	$: whiteObject =
		typeof white !== 'undefined' && nightMode?.white !== null
			? {
					value: nightMode.white,
					icon: 'fas fa-dot-circle',
					name: 'white',
					type: undefined,
				}
			: undefined;

	$: rgbObject =
		typeof rgb !== 'undefined' && nightMode?.rgb !== null
			? {
					name: 'color',
					icon: 'fa-duotone fa-light fa-circle',
					iconStyle: rgb
						? `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${rgb[0]},${rgb[1]},${rgb[2]}); --fa-secondary-opacity: 1`
						: undefined,
					type: undefined,
				}
			: undefined;
	$: ctObject =
		typeof ct !== 'undefined' && nightMode?.ct !== null
			? {
					name: 'color_temp',
					icon: 'fa-duotone fa-light fa-circle',
					iconStyle: `--fa-primary-color: var(--color-white); --fa-secondary-color: rgb(${kelvin_to_rgb(nightMode.ct)[0]},${
						kelvin_to_rgb(nightMode.ct)[1]
					},${kelvin_to_rgb(nightMode.ct)[2]}); --fa-secondary-opacity: 1`,

					type: undefined,
				}
			: undefined;

	$: labelList = [timeObject, brightnessObject, whiteObject, rgbObject, ctObject].filter(
		(l) => typeof l !== 'undefined',
	);

	$: if (disabled) {
		labelList.forEach((label) =>
			typeof label !== 'undefined' ? (label.type = 'disabled') : undefined,
		);
	} else {
		labelList.forEach((label) =>
			typeof label !== 'undefined' ? (label.type = undefined) : undefined,
		);
	}
</script>

<AdvancedCard title={$format('settings_title_night_mode', { default: '' })}>
	<LabelList size="m" {labelList} slot="main" />
	<ActionsContainer orientation="horizontal" type="simple" slot="right">
		<IconButton
			aria-label={$format('button_edit', { default: 'Edit' })}
			icon="fas fa-pen"
			slot="first"
			variant="primary"
			on:click={() => dispatcher('edit')}
		/>
	</ActionsContainer>
</AdvancedCard>
